/**
 * Zulu catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade isiZulu at a mathematician's register. **Warrant → «Isiqinisekiso»**
 * (justification/warrant — flagged). Scientific loanwords are used where isiZulu standardly
 * borrows (logic → Ilojiki, modal → Imodali, heuristic, scalar, geometry). W/M grades stay
 * Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other (`one` covers
 * 0–1); the noun uses the umu-/imi- class (umkhawulo → imikhawulo). `frameActive` declares
 * one/other plus an explicit `=0`. Per INV-STABLE-ID a wrong translation is a presentation
 * concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const ZU_CATALOG = {
    locale: 'zu',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Uphenyo',
        [P2.sectionClaims]: 'Izimangalo',
        [P2.sectionIntegrity]: 'Ubuqotho',
        [P2.axisWarrantLabel]: 'Isiqinisekiso',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Okokuhlolwa',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Incazelo} interp {Ukuhumusha} testable {Okuhlolekayo} tested {Okuhloliwe} refuted {Okuphikisiwe} program {Uhlelo locwaningo} philos {Okwefilosofi} other {Akwaziwa}}',
        [P2.axisModalLabel]: 'Imodali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Izinga lamandla {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Okuhlolisisekayo',
        [P2.stateRefuted]: 'Okuphikisiwe',
        [P2.stateOutside]: 'Ngaphandle kwesilinganiso',
        [P2.verdictConflict]: 'Iyaphikisana ne-{constraints}',
        [P2.verdictOutside]: 'Idinga i-{missing}',
        [P2.lossRegisterPlain]: 'Yenziwe lula olimini olujwayelekile — eminye imininingwane yokunemba ishiyiwe.',
        [P2.lossRegisterKid]: 'Yenziwe lula kubafundi abancane — imininingwane eminingi ishiyiwe.',
        [P2.lossProfileDetail]: 'Iphrofayela ephelele ye-Isiqinisekiso / Okokuhlolwa / Imodali ifinyezwe lapha.',
        [P2.lossConflictDetail]: 'Imininingwane yokungqubuzana ifinyeziwe kulokhu okubukwayo.',
        [P2.laneVerified]: 'Kuqinisekisiwe',
        [P2.laneUnverified]: 'Akuqinisekiswanga',
        [P2.laneConflict]: 'Ukungqubuzana kufakazelwe',
        [P2.constraintLogic]: 'Ilojiki',
        [P2.constraintLightBends]: 'Ukukhanya kuyagoba enkambini yamandla adonsayo',
        [P2.constraintParallelPostulate]: 'I-postulate yemigqa efanayo',
        [P2.claimScalarGravity]: 'Ithiyori ye-scalar yamandla adonsayo',
        [P2.claimNonEuclidean]: 'Ijiyomethri engeyona eka-Euclid',
        [P2.claimSubstrateSr]: 'Ukuhumusha kwe-substrate kobudlelwano obukhethekile',
        [P2.edgeExtObservation]: 'Ukubuka kwangaphandle',
        [P2.edgeResourceSample]: 'Insiza yekhompiyutha eyabelwene',
        [P2.edgeHeuristicSample]: 'Isiqondiso se-heuristic',
        [P2.edgeSharedSample]: 'Isisekelo esabiwe',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Imikhawulo',
        [P2.sectionEdges]: 'Imiphetho',
        [P2.sectionFrame]: 'Uhlaka',
        [P2.sectionExposure]: 'Ukuvezwa',
        [P2.statusAdoptedFormal]: 'Yamukelwe (esemthethweni)',
        [P2.statusAdoptedInterpretive]: 'Yamukelwe (ngokuhumusha)',
        [P2.statusSuspended]: 'Imisiwe',
        [P2.edgeEffectConstraint]: 'Yakha umkhawulo (ingaphikisa)',
        [P2.edgeEffectResource]: 'Insiza — ayinamthelela ezinqumweni',
        [P2.edgeEffectHeuristic]: 'I-heuristic — ayinamthelela ezinqumweni',
        [P2.edgeEffectShared]: 'Isisekelo esabiwe — ayinamthelela ezinqumweni',
        [P2.frameActive]: '{count, plural, =0 {Ayikho imikhawulo esebenzayo} one {# umkhawulo osebenzayo} other {# imikhawulo esebenzayo}}',
        [P2.exposureNone]: 'Akukho ukuncika kokuhumusha',
        [P2.exposureDep]: 'Incike ku-{id}',
        [P2.laneTrackMachine]: 'Umshini',
        [P2.laneTrackExpert]: 'Uchwepheshe',
        [P2.laneTrackCommunity]: 'Umphakathi',
        [P2.expertAudited]: 'Kuhloliwe',
        [P2.expertInProgress]: 'Kuyaqhubeka',
        [P2.expertNotReviewed]: 'Akubuyekezwanga',
        [P2.communityFlags]: 'Kukhona izimpawu',
        [P2.communityNone]: 'Azikho izimpawu',
        [P2.controlChallenge]: 'Phikisa',
        [P2.controlActivate]: 'Yenza isebenze',
        [P2.controlMakeInterpretive]: 'Yenza ibe ngeyokuhumusha',
        [P2.controlImportEdge]: 'Ngenisa umphetho',
        [P2.controlPromote]: 'Khuphula',
        [P2.controlUndo]: 'Hlehlisa',
        [P2.controlRedo]: 'Yenza futhi',
        [P2.controlRegister]: 'Bhalisa',
        [P2.promoteRejected]: 'Ukukhushulwa kwaliwe',
        [P2.promoteAccepted]: 'Ukukhushulwa kwamukelwe',
        [P2.promoteReasonRefutationTerminal]: 'Ubufakazi abukwazi ukwelapha ukuphikiswa.',
        [P2.promoteReasonKeystone]: 'Ukukhushulwa okubalulekile kudinga ukwesekwa kwe-empirical.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Uhlobo lwe-empirical alulungisiwe.',
        [P2.promoteReasonModalNotFixed]: 'Izinga le-modal alilungisiwe.',
        [P2.promoteReasonWarrantNotImproved]: 'Izinga lesiqinisekiso alithuthukisiwe.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=zu.js.map